import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DataTable,
  Input,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  MultiSelectCheckboxTag,
  Switch,
  SingleSelect,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Edit } from "lucide-react";
import { apiClientDrawerV2 as apiClient } from "@lift-admin/utils";
import { Toastify } from 'lift-ui';
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";

/** Basic email format: local@domain.tld */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getSandboxFieldSpecs(reportsList: any[] = []): FieldSpec[] {
  return [
    { key: "display_name", label: "Display Name" },
    { key: "embedded_url", label: "Embedded URL" },
    { key: "description", label: "Description" },
    { key: "maintainer", label: "Maintainer" },
    { key: "contact_info", label: "Contact Info" },
    {
      key: "linked_report_id",
      label: "Linked Report",
      resolve: (id) => {
        if (id == null || id === "") return "Not Set";
        return (
          reportsList.find((r: any) => String(r.id) === String(id))
            ?.display_name || String(id)
        );
      },
    },
  ];
}

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test((value || "").trim());
}

/** Validate contact info: empty is valid; if non-empty, must be one or more valid emails (comma or semicolon separated). */
function validateContactInfo(value: string | null | undefined): boolean {
  const trimmed = (value || "").trim();
  if (!trimmed) return true;
  const parts = trimmed.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 && parts.every(isValidEmail);
}

/** Used-in entry from GET /api/admin/reports (where this report is used). */
export type ReportUsageEntry = {
  application: { id: string; name: string };
  channel: { id: string; name: string };
  grouping: { id: string; name: string } | null;
  item: { id: string; display_name: string };
};

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: any;
  applications?: any[];
  channels?: any[];
  reports?: any[];
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const ReportsSandbox = ({
  toggle,
  searchText,
  openDialog,
  setOpenDialog,
  applications = [],
  channels = [],
  reports = [],
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemsBackup] = useState<any>([]);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const [currentIdData, setCurrentIdData] = useState<any>(null);
  const [display_name, setDisplayName] = useState<string | null>();
  const [maintainer, setMaintainer] = useState<string | null>();
  const [contact_info, setContactInfo] = useState<string | null>();
  const [url_type, setURLType] = useState<string | null>();
  const [embedded_url, setEmbededURL] = useState<string | null>();
  const [description, setDescription] = useState<string | null>();
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>("");
  const [selectedChannelId, setSelectedChannelId] = useState<string>("");
  const [linkedReportId, setLinkedReportId] = useState<string>("");
  const [filterApplicationId, setFilterApplicationId] = useState<string>("");
  const [filterChannelId, setFilterChannelId] = useState<string>("");
  const [searchedResults, setSearchedResults] = useState<any>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Column visibility for table
  const SANDBOX_SELECTABLE_COLUMN_KEYS = [
    "application", "channel", "grouping", "used_in_item", "linked_report",
    "display_name", "embedded_url", "description", "owner",
    "maintainer", "contact_info",
    "modified_by", "created_at", "updated_at",
  ];
  const [selectedSandboxColumns, setSelectedSandboxColumns] = useState<string[]>(() =>
    SANDBOX_SELECTABLE_COLUMN_KEYS.filter((k) => !["modified_by", "created_at", "updated_at"].includes(k))
  );

  // Channels for table filter (by selected application)
  const filterChannelsByApplication = useMemo(() => {
    if (!filterApplicationId) return channels || [];
    return (channels || []).filter(
      (ch: any) => String(ch.application_id) === String(filterApplicationId)
    );
  }, [channels, filterApplicationId]);

  const getApplicationNameForChannel = (channel: any) => {
    if (!channel?.application_id || !applications?.length) return null;
    const app = applications.find((a: any) => String(a.id) === String(channel.application_id));
    return app ? (app.display_name ?? app.name) || String(app.id) : null;
  };

  const channelLabelWithParent = (ch: any) => {
    const name = ch.name || String(ch.id);
    const appName = getApplicationNameForChannel(ch);
    return appName ? `${name} (${appName})` : name;
  };

  const channelsForApplication = useMemo(() => {
    console.log("[ReportsSandbox] channelsForApplication: selectedApplicationId =", selectedApplicationId, "channels count =", (channels || []).length);
    if (!selectedApplicationId) {
      console.log("[ReportsSandbox] channelsForApplication: no application selected, returning []");
      return [];
    }
    const filtered = (channels || []).filter(
      (ch: any) => String(ch.application_id) === String(selectedApplicationId)
    );
    console.log("[ReportsSandbox] channelsForApplication: filtered channels =", filtered.length, "sample channel keys:", filtered[0] ? Object.keys(filtered[0]) : [], "first channel application_id:", filtered[0]?.application_id);
    return filtered;
  }, [channels, selectedApplicationId]);

  const reportsForApplicationAndChannel = useMemo(() => {
    console.log("[ReportsSandbox] reportsForApplicationAndChannel: selectedApplicationId =", selectedApplicationId, "selectedChannelId =", selectedChannelId, "reports count =", (reports || []).length);
    if (!selectedApplicationId || !selectedChannelId) {
      console.log("[ReportsSandbox] reportsForApplicationAndChannel: missing app or channel, returning []");
      return [];
    }
    const reportsList = reports || [];
    const hasAnyUsedIn = reportsList.some((r: any) => (r.used_in?.length ?? 0) > 0);
    const filtered = reportsList.filter((r: any) => {
      const isParentReport = r.is_sandbox === false || r.isSandbox === false || !(r.is_sandbox ?? r.isSandbox);
      if (!isParentReport) return false;
      const usedIn = r.used_in || [];
      if (usedIn.length === 0) {
        // API did not return used_in (e.g. list endpoint omits it) — include all parent reports in dropdown so user can pick one
        return true;
      }
      return usedIn.some(
        (u: any) =>
          String(u.application?.id) === String(selectedApplicationId) &&
          String(u.channel?.id) === String(selectedChannelId)
      );
    });
    if (reportsList.length > 0 && filtered.length === 0) {
      const sample = reportsList[0];
      console.log("[ReportsSandbox] reportsForApplicationAndChannel: 0 matches. Sample report:", {
        id: sample?.id,
        display_name: sample?.display_name,
        is_sandbox: sample?.is_sandbox,
        isSandbox: sample?.isSandbox,
        used_in: sample?.used_in,
        "used_in[0]": sample?.used_in?.[0],
        "used_in[0].application": sample?.used_in?.[0]?.application,
        "used_in[0].channel": sample?.used_in?.[0]?.channel,
      });
    } else {
      console.log("[ReportsSandbox] reportsForApplicationAndChannel: filtered count =", filtered.length, "hasAnyUsedIn =", hasAnyUsedIn);
    }
    return filtered;
  }, [reports, selectedApplicationId, selectedChannelId]);

  // Debug: log when reports/applications/channels props change
  useEffect(() => {
    console.log("[ReportsSandbox] Props received:", {
      applicationsCount: (applications || []).length,
      channelsCount: (channels || []).length,
      reportsCount: (reports || []).length,
      sampleReport: (reports || [])[0] ? {
        id: (reports as any[])[0]?.id,
        display_name: (reports as any[])[0]?.display_name,
        is_sandbox: (reports as any[])[0]?.is_sandbox,
        isSandbox: (reports as any[])[0]?.isSandbox,
        used_in: (reports as any[])[0]?.used_in,
        used_in_keys: (reports as any[])[0]?.used_in?.[0] ? Object.keys((reports as any[])[0].used_in[0]) : [],
      } : null,
      sampleChannel: (channels || [])[0] ? { id: (channels as any[])[0]?.id, application_id: (channels as any[])[0]?.application_id, name: (channels as any[])[0]?.name } : null,
      sampleApplication: (applications || [])[0] ? { id: (applications as any[])[0]?.id, name: (applications as any[])[0]?.name, display_name: (applications as any[])[0]?.display_name } : null,
    });
  }, [reports, applications, channels]);

  // Debug: log when Add/Edit sheet opens — dropdown state for Report
  useEffect(() => {
    if (openDialog || openEditDialog) {
      console.log("[ReportsSandbox] Sheet open — dropdown state:", {
        openDialog,
        openEditDialog,
        selectedApplicationId,
        selectedChannelId,
        channelsForApplicationCount: channelsForApplication.length,
        reportsForApplicationAndChannelCount: reportsForApplicationAndChannel.length,
        reportOptions: reportsForApplicationAndChannel.map((r: any) => ({ id: r.id, display_name: r.display_name })),
      });
    }
  }, [openDialog, openEditDialog, selectedApplicationId, selectedChannelId, channelsForApplication.length, reportsForApplicationAndChannel.length]);

  const getReports = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClient.get("/admin/reports", {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
          is_sandbox: true,
        }
      });

      const drawerReport = res?.data?.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );
      
      if (searchText.trim().length === 0) {
        setDrawerItems(drawerReport);
      } else {
        setDrawerItems(
          drawerReport.filter((item: any) => searchedResults.includes(item.id))
        );
      }

      setDrawerItemsBackup(drawerReport);
    } catch {
      Toastify("error", "Something went wrong");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    getReports();
  }, []);

  const resetState = () => {
    setDisplayName("");
    setURLType("");
    setEmbededURL("");
    setDescription("");
    setSelectedApplicationId("");
    setSelectedChannelId("");
    setLinkedReportId("");
    setCurrentIdData(null);
    setMaintainer("");
    setContactInfo("");
  };

  const handleDialogClose = () => {
    setOpenDialog((prev: any) => ({
      ...prev,
      reportSandbox: false,
    }));
    resetState();
  };

  const handleAddChannel = async () => {
    try {
      if (!display_name || display_name.length == 0) {
        Toastify("error", "Please enter correct Display name");
        return;
      }

      if (!embedded_url || embedded_url.length == 0) {
        Toastify("error", "Please enter correct Embeded Url");
        return;
      }

      if (!description || description.length == 0) {
        Toastify("error", "Please enter correct description");
        return;
      }

      if (contact_info?.trim() && !validateContactInfo(contact_info)) {
        Toastify("error", "Please enter a valid email address for Contact Info (e.g. name@example.com). Multiple emails can be separated by comma or semicolon.");
        return;
      }

      setLoadData(true);
      const user = JSON.parse(localStorage.getItem("user") || "");
      const payload = {
        display_name,
        embedded_url,
        url_type: "Sandbox",
        description,
        maintainer: maintainer || "",
        contact_info: contact_info || "",
        linked_report_id: linkedReportId || null,
        owner: {
          id: user.id,
          name: user.name || "",
          email: user.email || ""
        },
        modified_by: user.id,
        created_by: user.id
      };

      const res = await apiClient.post("/admin/reports", payload, {
        params: {
          user_id: user.id,
          brand_id: user.brand_id,
          is_admin: true,
          is_team: false,
        }
      });

      getReports();
      resetState();
      setOpenDialog(false);
      Toastify("success", "Report added successfully");

      const sandboxFieldSpecs = getSandboxFieldSpecs(reports);
      const createdSandbox = {
        display_name: payload.display_name,
        embedded_url: payload.embedded_url,
        description: payload.description,
        maintainer: payload.maintainer,
        contact_info: payload.contact_info,
        linked_report_id: payload.linked_report_id,
      };
      const desc = {
        message: buildSummary("Report sandbox", payload?.display_name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_report_sandbox",
        reportId: res.data?.id ?? null,
        reportName: payload?.display_name,
        details: buildDetails(createdSandbox, sandboxFieldSpecs, {
          skipEmpty: true,
        }),
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      console.log(err, "error");
      if (err.response?.data?.message === "Validation failed") {
        Toastify("error", err.response.data.errors[0].message);
      } else {
        Toastify("error", err.response?.data?.detail || "Something went wrong");
      }
      const errorDesc = {
        message: `Failed to create report sandbox "${display_name || ""}"`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/reports",
        category: "drawer",
        action: "create_report_sandbox",
        reportName: display_name,
        error:
          err.response?.data?.errors?.[0]?.message ||
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleDelete = async (data: any) => {
    try {
      if (!data?.id || isNaN(data?.id)) {
        Toastify("error", "Invalid Report ID. Please select correct report");
        return;
      }

      setLoadData(true);
      const user = JSON.parse(localStorage.getItem("user") || "");
      const res = await apiClient.delete(`/admin/reports`, {
        params: {
          user_id: user.id,
          brand_id: user.brand_id,
          is_admin: true,
          is_team: false,
        }
      });

      getReports();
      setOpenDeleteDialog(false);
      Toastify("success", "Item Deleted successfully");

      const sandboxFieldSpecs = getSandboxFieldSpecs(reports);
      const desc = {
        message: buildSummary("Report sandbox", data?.display_name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_report_sandbox",
        reportId: data?.id,
        reportName: data?.display_name,
        details: buildDetails(
          {
            display_name: data?.display_name,
            embedded_url: data?.embedded_url,
            description: data?.description,
            maintainer: data?.maintainer,
            contact_info: data?.contact_info,
            linked_report_id: data?.linked_report_id ?? null,
          },
          sandboxFieldSpecs,
          { skipEmpty: true }
        ),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", "Report Sandbox not deleted");
      const errorDesc = {
        message: `Failed to delete report sandbox "${data?.display_name || ""}"`,
        status: err.response?.status,
        method: "DELETE",
        url: "/admin/reports",
        category: "drawer",
        action: "delete_report_sandbox",
        reportId: data?.id,
        reportName: data?.display_name,
        error: err.response?.data?.message || err.response?.data?.detail || err.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleEdit = async () => {
    if (!currentIdData?.id) {
      Toastify("error", "Invalid report ID. Please select correct report");
      return;
    }

    if (!display_name || display_name.length == 0) {
      Toastify("error", "Please enter correct Display name");
      return;
    }

    if (!embedded_url || embedded_url.length == 0) {
      Toastify("error", "Please enter correct Embeded Url");
      return;
    }

    if (!description || description.length == 0) {
      Toastify("error", "Please enter correct description");
      return;
    }

    if (contact_info?.trim() && !validateContactInfo(contact_info)) {
      Toastify("error", "Please enter a valid email address for Contact Info (e.g. name@example.com). Multiple emails can be separated by comma or semicolon.");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "");
    try {
      setLoadData(true);
      const payload = {
        id: currentIdData.id,
        display_name,
        embedded_url,
        url_type: "Sandbox",
        description,
        maintainer: maintainer || "",
        contact_info: contact_info || "",
        linked_report_id: linkedReportId || currentIdData?.linked_report_id || null,
        owner: currentIdData.owner || {
          id: user.id,
          name: user.name || "",
          email: user.email || ""
        },
        modified_by: user.id,
        created_by: currentIdData.created_by || user.id,
        created_at: currentIdData.created_at,
        updated_at: new Date().toISOString()
      };

      const res = await apiClient.post(`/admin/reports`, payload, {
        params: {
          user_id: user.id,
          brand_id: user.brand_id,
          is_admin: true,
          is_team: false,
        }
      });

      Toastify("success", "Item updated successfully");
      const sandboxFieldSpecs = getSandboxFieldSpecs(reports);
      const previousSandbox = {
        display_name: currentIdData?.display_name,
        embedded_url: currentIdData?.embedded_url,
        description: currentIdData?.description,
        maintainer: currentIdData?.maintainer,
        contact_info: currentIdData?.contact_info,
        linked_report_id: currentIdData?.linked_report_id ?? null,
      };
      const nextSandbox = {
        display_name,
        embedded_url,
        description,
        maintainer: maintainer || "",
        contact_info: contact_info || "",
        linked_report_id: linkedReportId || currentIdData?.linked_report_id || null,
      };
      getReports();
      handleEditDialogClose();

      const changes = diffFields(
        previousSandbox,
        nextSandbox,
        sandboxFieldSpecs
      );
      const desc = {
        message: buildSummary(
          "Report sandbox",
          display_name || currentIdData?.display_name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_report_sandbox",
        reportId: currentIdData?.id,
        reportName: display_name || currentIdData?.display_name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      console.log(err, "error");
      if (err.response?.data?.message === "Validation failed") {
        Toastify("error", err.response.data.errors[0].message);
      } else {
        Toastify("error", err.response?.data?.detail || "Something went wrong");
      }
      const errorDesc = {
        message: `Failed to update report sandbox "${display_name || currentIdData?.display_name || ""}"`,
        status: err.response?.status,
        method: "POST",
        url: "/admin/reports",
        category: "drawer",
        action: "update_report_sandbox",
        reportId: currentIdData?.id,
        reportName: display_name || currentIdData?.display_name,
        error:
          err.response?.data?.errors?.[0]?.message ||
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditOpen = (data: any) => {
    setCurrentIdData(data);
    setDisplayName(data?.display_name);
    setEmbededURL(data?.embedded_url);
    setDescription(data?.description);
    setURLType(data?.url_type);
    setMaintainer(data?.maintainer);
    setContactInfo(data?.contact_info);
    setLinkedReportId(data?.linked_report_id != null ? String(data.linked_report_id) : "");
    const linkedId = data?.linked_report_id != null ? String(data.linked_report_id) : "";
    if (linkedId && reports?.length) {
      const linkedReport = (reports as any[])?.find((r: any) => String(r.id) === linkedId);
      const firstUsedIn = linkedReport?.used_in?.[0];
      if (firstUsedIn) {
        setSelectedApplicationId(String(firstUsedIn.application?.id ?? ""));
        setSelectedChannelId(String(firstUsedIn.channel?.id ?? ""));
      } else {
        setSelectedApplicationId("");
        setSelectedChannelId("");
      }
    } else {
      setSelectedApplicationId("");
      setSelectedChannelId("");
    }
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setDisplayName("");
    setEmbededURL("");
    setDescription("");
    setURLType("");
    setSelectedApplicationId("");
    setSelectedChannelId("");
    setLinkedReportId("");
    setCurrentIdData(null);
    setOpenEditDialog(false);
    setMaintainer("");
    setContactInfo("");
  };

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerItemBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["display_name"]
          ?.toLowerCase()
          .includes(query.toLowerCase()) ||
        filteredUsers[i]["embedded_url"]
          ?.toLowerCase()
          .includes(query.toLowerCase()) ||
        filteredUsers[i]["url_type"]
          ?.toLowerCase()
          .includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }
    setSearchedResults(results.map((item: any) => item.id));
    setDrawerItems(results);
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText]);

  useEffect(() => {
    setPage(0);
  }, [filterApplicationId, filterChannelId]);

  // Filter sandbox reports by application and/or channel (used_in)
  const filteredSandboxReports = useMemo(() => {
    let list = drawerItem || [];
    if (filterApplicationId) {
      list = list.filter((row: any) =>
        (row.used_in || []).some((u: any) => String(u.application?.id) === String(filterApplicationId))
      );
    }
    if (filterChannelId) {
      list = list.filter((row: any) =>
        (row.used_in || []).some((u: any) => String(u.channel?.id) === String(filterChannelId))
      );
    }
    return list;
  }, [drawerItem, filterApplicationId, filterChannelId]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredSandboxReports.slice(start, end);
  }, [filteredSandboxReports, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredSandboxReports.length / rowsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const sandboxColumnLabels: Record<string, string> = {
    display_name: "Display Name",
    embedded_url: "Embedded URL",
    description: "Description",
    owner: "Owner",
    maintainer: "Maintainer",
    contact_info: "Contact Info",
    application: "Application",
    channel: "Channel",
    grouping: "Grouping",
    used_in_item: "Used in (Item)",
    linked_report: "Linked Report",
    modified_by: "Modified By",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const linkedReportDisplayName = (row: any) => {
    const id = row?.linked_report_id;
    if (!id || !reports?.length) return "—";
    const linked = (reports as any[]).find((r: any) => String(r.id) === String(id));
    return linked?.display_name ?? "—";
  };

  const usedInApplications = (usedIn: ReportUsageEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.application?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };
  const usedInChannels = (usedIn: ReportUsageEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.channel?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };
  const usedInGroupings = (usedIn: ReportUsageEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.grouping?.name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };
  const usedInItems = (usedIn: ReportUsageEntry[] | undefined) => {
    if (!usedIn?.length) return "—";
    const names = [...new Set(usedIn.map((u) => u.item?.display_name).filter(Boolean))];
    return names.length ? names.join(", ") : "—";
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex flex-wrap items-end justify-between gap-4 pb-3">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5 min-w-[160px]">
                <Label className="text-sm font-medium">Application</Label>
                <SingleSelect
                  options={[
                    { label: "All applications", value: "all" },
                    ...(applications || []).map((a: any) => ({
                      label: (a.display_name ?? a.name) || String(a.id),
                      value: String(a.id),
                    })),
                  ]}
                  defaultValue={filterApplicationId || "all"}
                  onChange={(value) => {
                    const val = value || "all";
                    setFilterApplicationId(val === "all" ? "" : val);
                    setFilterChannelId("");
                  }}
                  placeholder="All applications"
                  containerClassName="w-full"
                />
              </div>
              <div className="space-y-1.5 min-w-[160px]">
                <Label className="text-sm font-medium">Channel</Label>
                <SingleSelect
                  options={[
                    { label: "All channels", value: "all" },
                    ...filterChannelsByApplication.map((ch: any) => ({
                      label: channelLabelWithParent(ch),
                      value: String(ch.id),
                    })),
                  ]}
                  defaultValue={filterChannelId || "all"}
                  onChange={(value) => {
                    const val = value || "all";
                    setFilterChannelId(val === "all" ? "" : val);
                  }}
                  placeholder="All channels"
                  containerClassName="w-full"
                />
              </div>
            </div>
            <div className="space-y-1.5 min-w-[180px] max-w-[280px]">
              <Label htmlFor="columns-sandbox" className="text-sm font-medium">
                Columns
              </Label>
              <MultiSelectCheckboxTag
                placeholder={selectedSandboxColumns.length > 0 ? `Columns (${selectedSandboxColumns.length})` : "Select Columns"}
                options={SANDBOX_SELECTABLE_COLUMN_KEYS.map((key) => ({ value: key, label: sandboxColumnLabels[key] ?? key }))}
                value={selectedSandboxColumns}
                onChange={(ids: string[]) => ids.length > 0 && setSelectedSandboxColumns(ids)}
                containerClassName="w-full"
                showSelectAll
              />
            </div>
          </div>
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="100%"
          columns={(() => {
            const dataCols: TableColumn<any>[] = [
              { key: "application", header: "Application", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInApplications(row.used_in)}>{usedInApplications(row.used_in)}</span> },
              { key: "channel", header: "Channel", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInChannels(row.used_in)}>{usedInChannels(row.used_in)}</span> },
              { key: "grouping", header: "Grouping", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInGroupings(row.used_in)}>{usedInGroupings(row.used_in)}</span> },
              { key: "used_in_item", header: "Used in (Item)", accessor: (row: any) => row.used_in, width: "140px", render: (_v: any, row: any) => <span className="block max-w-[140px] truncate" title={usedInItems(row.used_in)}>{usedInItems(row.used_in)}</span> },
              { key: "linked_report", header: "Linked Report", accessor: (row: any) => row.linked_report_id, width: "160px", render: (_v: any, row: any) => <span className="block max-w-[160px] truncate" title={linkedReportDisplayName(row)}>{linkedReportDisplayName(row)}</span> },
              { key: "display_name", header: "Display Name", accessor: "display_name", width: "180px", render: (_v: any, row: any) => <span className="font-medium">{row.display_name}</span> },
              { key: "embedded_url", header: "Embedded URL", accessor: "embedded_url", width: "200px", render: (_v: any, row: any) => <span className="block max-w-[200px] truncate" title={row.embedded_url}>{row.embedded_url}</span> },
              { key: "description", header: "Description", accessor: "description", width: "200px", render: (_v: any, row: any) => <span className="block max-w-[200px] truncate" title={row.description}>{row.description}</span> },
              { key: "owner", header: "Owner", accessor: (row: any) => row.owner?.name || "", width: "120px" },
              { key: "maintainer", header: "Maintainer", accessor: "maintainer", width: "120px" },
              { key: "contact_info", header: "Contact Info", accessor: "contact_info", width: "120px" },
              { key: "modified_by", header: "Modified By", accessor: (row: any) => row.modified_by?.name || "", width: "120px" },
              { key: "created_at", header: "Created At", accessor: (row: any) => formatDate(row.created_at), width: "140px" },
              { key: "updated_at", header: "Updated At", accessor: (row: any) => formatDate(row.updated_at), width: "140px" },
            ];
            const filtered = SANDBOX_SELECTABLE_COLUMN_KEYS.filter((k) => selectedSandboxColumns.includes(k))
              .map((k) => dataCols.find((c) => c.key === k))
              .filter(Boolean) as TableColumn<any>[];
            if (showActionsColumn) {
              filtered.push({
                key: "actions",
                header: "Actions",
                accessor: (row: any) => row.id,
                width: "120px",
                align: "center",
                render: (_v: any, row: any) => (
                  <div className="flex gap-1 justify-center">
                    {hasEditAccess && (
                      <Button variant="ghost" size="sm" onClick={() => handleEditOpen(row)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ),
              });
            }
            return filtered;
          })()}
          data={paginatedData}
          loading={loadData}
          emptyMessage="No sandbox reports found."
          stickyHeader
          showActions={false}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: filteredSandboxReports.length,
            onPageChange: (p) => setPage(p - 1),
            onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
            pageSizeOptions: [10, 20, 50, 100],
          }}
          />
        </div>
      </div>

      {/* Add New Report Sheet */}
      <Sheet open={openDialog} onOpenChange={(open) => {
        if (!open) handleDialogClose();
      }}>
        <SheetContent className="sm:max-w-[500px] flex flex-col h-full">
          <SheetHeader className="flex flex-row items-center justify-between gap-4">
            <SheetTitle>Add New Sandbox Report</SheetTitle>
            {/* Team Access toggle aligned with close icon */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Team Access</span>
              <Switch
                checked={false}
                onCheckedChange={() => {}}
              />
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Application</Label>
              <SingleSelect
                options={(applications || []).map((a: any) => ({
                  label: (a.display_name ?? a.name) || String(a.id),
                  value: String(a.id),
                }))}
                defaultValue={selectedApplicationId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setSelectedApplicationId(val);
                  setSelectedChannelId("");
                  setLinkedReportId("");
                }}
                placeholder="Select application"
                containerClassName="w-full"
              />
            </div>
            <div className="grid gap-2">
              <Label>Channel</Label>
              <SingleSelect
                options={channelsForApplication.map((ch: any) => ({
                  label: channelLabelWithParent(ch),
                  value: String(ch.id),
                }))}
                defaultValue={selectedChannelId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setSelectedChannelId(val);
                  setLinkedReportId("");
                }}
                placeholder="Select channel"
                containerClassName="w-full"
                disabled={!selectedApplicationId}
              />
            </div>
            <div className="grid gap-2">
              <Label>Linked report (optional)</Label>
              <SingleSelect
                options={reportsForApplicationAndChannel.map((r: any) => ({
                  label: r.display_name || String(r.id),
                  value: String(r.id),
                }))}
                defaultValue={linkedReportId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setLinkedReportId(val);
                }}
                placeholder="Select report to link"
                containerClassName="w-full"
                disabled={!selectedChannelId}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                value={display_name || ""}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="embedded_url">Embedded URL *</Label>
              <Input
                id="embedded_url"
                value={embedded_url || ""}
                onChange={(e) => setEmbededURL(e.target.value)}
                placeholder="Enter embedded URL"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={description || ""}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="maintainer">Maintainer</Label>
              <Input
                id="maintainer"
                value={maintainer || ""}
                onChange={(e) => setMaintainer(e.target.value)}
                placeholder="Enter maintainer"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_info">Contact Info (email)</Label>
              <Input
                id="contact_info"
                value={contact_info || ""}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="e.g. name@example.com (comma/semicolon for multiple)"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button variant="outline" onClick={handleDialogClose} disabled={loadData}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loadData}>
              {loadData ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Report Sheet */}
      <Sheet open={openEditDialog} onOpenChange={(open) => {
        if (!open) handleEditDialogClose();
      }}>
        <SheetContent className="sm:max-w-[500px] flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Edit Sandbox Report</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Application</Label>
              <SingleSelect
                options={(applications || []).map((a: any) => ({
                  label: (a.display_name ?? a.name) || String(a.id),
                  value: String(a.id),
                }))}
                defaultValue={selectedApplicationId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setSelectedApplicationId(val);
                  setSelectedChannelId("");
                  setLinkedReportId("");
                }}
                placeholder="Select application"
                containerClassName="w-full"
              />
            </div>
            <div className="grid gap-2">
              <Label>Channel</Label>
              <SingleSelect
                options={channelsForApplication.map((ch: any) => ({
                  label: channelLabelWithParent(ch),
                  value: String(ch.id),
                }))}
                defaultValue={selectedChannelId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setSelectedChannelId(val);
                  setLinkedReportId("");
                }}
                placeholder="Select channel"
                containerClassName="w-full"
                disabled={!selectedApplicationId}
              />
            </div>
            <div className="grid gap-2">
              <Label>Linked report (optional)</Label>
              <SingleSelect
                options={reportsForApplicationAndChannel.map((r: any) => ({
                  label: r.display_name || String(r.id),
                  value: String(r.id),
                }))}
                defaultValue={linkedReportId || undefined}
                onChange={(value) => {
                  const val = value || "";
                  setLinkedReportId(val);
                }}
                placeholder="Select report to link"
                containerClassName="w-full"
                disabled={!selectedChannelId}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_display_name">Display Name *</Label>
              <Input
                id="edit_display_name"
                value={display_name || ""}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_embedded_url">Embedded URL *</Label>
              <Input
                id="edit_embedded_url"
                value={embedded_url || ""}
                onChange={(e) => setEmbededURL(e.target.value)}
                placeholder="Enter embedded URL"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_description">Description *</Label>
              <Input
                id="edit_description"
                value={description || ""}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_maintainer">Maintainer</Label>
              <Input
                id="edit_maintainer"
                value={maintainer || ""}
                onChange={(e) => setMaintainer(e.target.value)}
                placeholder="Enter maintainer"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit_contact_info">Contact Info (email)</Label>
              <Input
                id="edit_contact_info"
                value={contact_info || ""}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="e.g. name@example.com (comma/semicolon for multiple)"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button variant="outline" onClick={handleEditDialogClose}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={loadData}>
              {loadData ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Updating...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete?</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(currentIdData)} disabled={loadData}>
              {loadData ? (
                <>
                  <SpinnerLoader className="mr-2 h-4 w-4" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportsSandbox;
